import { z } from 'zod';

export const productAvailabilitySchema = z.enum(['candidate_slot', 'canary', 'active', 'paused', 'sold_out']);
export type ProductAvailability = z.infer<typeof productAvailabilitySchema>;

export const storeProductSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  problem: z.string().min(1),
  category: z.enum(['desk_cable', 'drawer_shelf', 'packing_pouch', 'surface_cleaning']),
  selectionMode: z.enum(['universal_utility', 'specific_delight']),
  delightLabel: z.string().optional(),
  audience: z.string().min(1),
  availability: productAvailabilitySchema,
  approvedSourceId: z.string().min(1).nullable(),
  sourceSku: z.string().min(1).nullable(),
  sourceCountry: z.enum(['KR', 'CN', 'JP']).nullable(),
  approvedFulfillmentProviderId: z.string().min(1).nullable(),
  fulfillmentSku: z.string().min(1).nullable(),
  returnSiteRef: z.string().min(1).nullable(),
  priceKrw: z.number().int().nonnegative().nullable(),
  shippingKrw: z.number().int().nonnegative().nullable(),
  dimensions: z.array(z.object({ label: z.string(), value: z.string(), verified: z.boolean() })),
  fitNotes: z.array(z.string()),
  notFor: z.array(z.string()),
  imageRoles: z.array(z.object({ role: z.string(), url: z.string(), alt: z.string(), status: z.enum(['ready', 'pending', 'failed']) })),
  contentPacketHash: z.string().nullable(),
  priceCheckedAt: z.string().datetime().nullable(),
  inventoryAvailable: z.number().int().nonnegative().nullable(),
  inventoryCheckedAt: z.string().datetime().nullable(),
  policyVersion: z.string(),
  salesEnabled: z.boolean(),
  blockedReasons: z.array(z.string())
});
export type StoreProduct = z.infer<typeof storeProductSchema>;

export const cartItemSchema = z.object({ productId: z.string().min(1), variantId: z.string().min(1), quantity: z.number().int().min(1).max(20) });
export const cartRevalidateSchema = z.object({ items: z.array(cartItemSchema).min(1).max(20), revision: z.string().optional() });
export const shippingQuoteSchema = z.object({ postalCode: z.string().regex(/^\d{5}$/), items: z.array(cartItemSchema).min(1).max(20) });
export const checkoutQuoteSchema = z.object({
  cartRevision: z.string().min(1),
  items: z.array(cartItemSchema).min(1).max(20),
  guest: z.object({ name: z.string().min(2).max(30), phone: z.string().regex(/^01[016789]-?\d{3,4}-?\d{4}$/), email: z.email().optional(), postalCode: z.string().regex(/^\d{5}$/), address1: z.string().min(4).max(120), address2: z.string().max(120).optional(), deliveryMemo: z.string().max(80).optional(), marketingOptIn: z.boolean().default(false) })
});
export const createOrderSchema = z.object({ quoteHash: z.string().startsWith('sha256:'), cartRevision: z.string().startsWith('sha256:'), idempotencyKey: z.string().startsWith('sha256:'), requiredTermsVersion: z.string().min(1) });

export const storeOrderStatusSchema = z.enum([
  'payment_pending',
  'paid',
  'fulfillment_pending',
  'shipped',
  'delivered',
  'cancel_requested',
  'cancelled',
  'return_requested',
  'exchange_requested',
  'return_in_transit',
  'refund_pending',
  'refunded'
]);
export type StoreOrderStatus = z.infer<typeof storeOrderStatusSchema>;

export const orderClaimKindSchema = z.enum(['cancel', 'return', 'exchange', 'delivery_issue', 'product_issue']);
export type OrderClaimKind = z.infer<typeof orderClaimKindSchema>;
export const orderClaimStateSchema = z.enum(['received', 'reviewing', 'fulfillment_contact_pending', 'pickup_scheduled', 'refund_pending', 'resolved', 'rejected']);
export type OrderClaimState = z.infer<typeof orderClaimStateSchema>;
export const createOrderClaimSchema = z.object({
  kind: orderClaimKindSchema,
  reasonCode: z.enum(['changed_mind', 'wrong_item', 'damaged', 'defective', 'missing_item', 'late_delivery', 'not_as_described', 'other']),
  detail: z.string().trim().min(8).max(500),
  idempotencyKey: z.string().startsWith('sha256:')
});

export type PublicOrderClaim = {
  id: string;
  kind: OrderClaimKind;
  reasonCode: z.infer<typeof createOrderClaimSchema>['reasonCode'];
  state: OrderClaimState;
  createdAt: string;
  updatedAt: string;
};

export type StoreOrderResponse = {
  order: {
    id: string;
    status: StoreOrderStatus;
    totalKrw: number;
    createdAt: string;
    paymentState: 'not_paid' | 'paid' | 'refunded';
    lines: Array<{ productId: string; title: string; quantity: number; unitPriceKrw: number }>;
    timeline: Array<{ status: StoreOrderStatus; label: string; at: string | null; current: boolean }>;
    availableActions: OrderClaimKind[];
    claims: PublicOrderClaim[];
  };
  seller: 'MUNOPS';
  supportOwner: 'MUNOPS';
};

export type StoreHomeResponse = {
  dataVersion: string;
  storeStatus: 'preparing' | 'open' | 'paused';
  publicBrandStatus: 'pending_owner_decision' | 'selected';
  products: StoreProduct[];
  utilityCount: number;
  delightCount: number;
  standards: string[];
};
